import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  Calendar as CalendarIcon,
  CreditCard,
  Eye,
  IndianRupee,
  Loader2,
  MapPin,
  Moon,
  ShieldAlert,
  Sun,
  TrendingUp,
  Users,
  UserPlus,
  Repeat,
  Target,
  Wallet,
  Clock,
  Activity,
  Star,
  CalendarX,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useMasterData } from '../contexts/MasterDataContext';
import { API } from '../utils/apiConfig';

// ---------- Helpers ----------
// Returns today's date as YYYY-MM-DD in India time (Asia/Kolkata), regardless of browser timezone
const istDateStr = (offsetDays = 0) => {
  const ist = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  if (!offsetDays) return ist;
  const d = new Date(`${ist}T00:00:00`);
  d.setDate(d.getDate() + offsetDays);
  return d.toLocaleDateString('en-CA');
};

const RANGE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'mtd', label: 'Month to date' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range…' },
];

const TIME_SLOT_PRESETS = [
  { value: 'all', label: 'All Hours' },
  { value: 'morning', label: 'Morning (6AM-12PM)' },
  { value: 'afternoon', label: 'Afternoon (12PM-6PM)' },
  { value: 'evening', label: 'Evening (6PM-10PM)' },
  { value: 'latenight', label: 'Late Night (10PM-2AM)' },
  { value: '4pm_12am', label: 'Evening Peak (4PM-12AM)' },
  { value: '6pm_12am', label: 'Night (6PM-12AM)' },
];

const inr = (n) => `₹${Math.round(parseFloat(n) || 0).toLocaleString('en-IN')}`;
const inrK = (n) => {
  const v = parseFloat(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v}`;
};

const SOURCE_COLORS = {
  Hudle: '#3b82f6',
  Khelomore: '#10b981',
  District: '#f59e0b',
  Direct: '#f59e0b',
  'Walk-in': '#f59e0b',
  WhatsApp: '#a855f7',
};
const fallbackColors = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ec4899', '#06b6d4', '#84cc16'];

const BOOKING_SOURCE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'Calling', label: 'Calling' },
  { value: 'App', label: 'App' },
  { value: 'WhatsApp', label: 'WhatsApp' },
  { value: 'Khelomore', label: 'Khelomore' },
  { value: 'District', label: 'District' },
  { value: 'Hudle', label: 'Hudle' },
  { value: 'Others', label: 'Others' },
];

const BULK_FILTER_OPTIONS = [
  { value: 'without', label: 'Without Bulk Booking' },
  { value: 'with', label: 'With Bulk Booking' },
  { value: 'only', label: 'Only Bulk Booking' },
];

const GAME_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ec4899', '#06b6d4'];

// ---------- Sub-components ----------
const DeltaPill = ({ pct, suffix = 'vs yesterday', isDark }) => {
  if (pct === null || pct === undefined) {
    return (
      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'} mt-1`}>
        {suffix}: —
      </p>
    );
  }
  const positive = pct >= 0;
  const arrow = positive ? '↑' : '↓';
  const cls = positive ? 'text-emerald-500' : 'text-rose-500';
  return (
    <p className={`text-xs ${cls} mt-1 font-medium`}>
      {arrow} {Math.abs(pct)}% {suffix}
    </p>
  );
};

const KpiCard = ({ icon: Icon, iconBg, iconColor, label, value, valueSize, deltaPct, deltaLabel, isDark }) => (
  <Card
    className={`p-4 ${isDark ? 'bg-slate-900/80 border-slate-800' : 'bg-white border-slate-200'} border`}
    data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
  >
    <div className="flex items-start gap-3">
      <div className={`p-3 rounded-xl ${iconBg} shrink-0`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
        <p className={`${valueSize || 'text-2xl'} font-bold ${isDark ? 'text-white' : 'text-slate-900'} mt-0.5 truncate`}>
          {value}
        </p>
        <DeltaPill pct={deltaPct} suffix={deltaLabel} isDark={isDark} />
      </div>
    </div>
  </Card>
);

// ---------- Page ----------
export default function DashboardPage() {
  const { token, isStaff } = useAuth();
  const { getVenueNames } = useMasterData();
  const navigate = useNavigate();

  const [range, setRange] = useState(() => (
    localStorage.getItem('dash-range') || 'today'
  ));
  // Staff role is locked to "today" — force-reset if it drifted from localStorage.
  useEffect(() => {
    if (isStaff && range !== 'today') setRange('today');
  }, [isStaff, range]);
  const [venue, setVenue] = useState(() => localStorage.getItem('dash-venue') || 'all');
  const [timeSlot, setTimeSlot] = useState(() => localStorage.getItem('dash-timeslot') || 'all');
  const [bookingSource, setBookingSource] = useState(() => localStorage.getItem('dash-source') || 'all');
  const [bulkFilter, setBulkFilter] = useState(() => localStorage.getItem('dash-bulk') || 'without');
  const [isDark, setIsDark] = useState(() => localStorage.getItem('dash-theme') === 'dark');
  const [customStart, setCustomStart] = useState(() => {
    const v = localStorage.getItem('dash-custom-start');
    if (v) return v;
    // Default: 14 days ago (India time)
    return istDateStr(-14);
  });
  const [customEnd, setCustomEnd] = useState(() => {
    return localStorage.getItem('dash-custom-end') || istDateStr();
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const venueOptions = useMemo(() => getVenueNames(), [getVenueNames]);

  useEffect(() => { localStorage.setItem('dash-range', range); }, [range]);
  useEffect(() => { localStorage.setItem('dash-venue', venue); }, [venue]);
  useEffect(() => { localStorage.setItem('dash-timeslot', timeSlot); }, [timeSlot]);
  useEffect(() => { localStorage.setItem('dash-source', bookingSource); }, [bookingSource]);
  useEffect(() => { localStorage.setItem('dash-bulk', bulkFilter); }, [bulkFilter]);
  useEffect(() => { localStorage.setItem('dash-theme', isDark ? 'dark' : 'light'); }, [isDark]);
  useEffect(() => { localStorage.setItem('dash-custom-start', customStart); }, [customStart]);
  useEffect(() => { localStorage.setItem('dash-custom-end', customEnd); }, [customEnd]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    // For custom range, both dates required and start ≤ end
    if (range === 'custom') {
      if (!customStart || !customEnd) return;
      if (customStart > customEnd) {
        toast.error('Start date must be on or before end date');
        return;
      }
    }
    setLoading(true);
    try {
      const params = { range, bulk_filter: bulkFilter };
      if (venue && venue !== 'all') params.venue = venue;
      if (timeSlot && timeSlot !== 'all') params.time_slot = timeSlot;
      if (bookingSource && bookingSource !== 'all') params.booking_source = bookingSource;
      if (range === 'custom') {
        params.start = customStart;
        params.end = customEnd;
      }
      const res = await axios.get(`${API}/dashboard/overview`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Dashboard fetch error', err);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [range, venue, token, customStart, customEnd, timeSlot, bookingSource, bulkFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---- Derived classes for theme ----
  const themeBg = isDark ? 'bg-slate-950' : 'bg-slate-50';
  const themeCard = isDark ? 'bg-slate-900/80 border-slate-800' : 'bg-white border-slate-200';
  const themeText = isDark ? 'text-white' : 'text-slate-900';
  const themeMuted = isDark ? 'text-slate-400' : 'text-slate-500';

  // ---- KPI rendering data ----
  const k = data?.kpis;
  const kpiData = k
    ? [
        { icon: IndianRupee, iconBg: 'bg-emerald-500/20', iconColor: 'text-emerald-400', label: 'Total Revenue (Today)', value: inr(k.total_revenue_today.value), deltaPct: k.total_revenue_today.delta_pct, deltaLabel: k.total_revenue_today.compare_label },
        // Staff role hides the month-to-date revenue KPI card
        ...(isStaff ? [] : [
          { icon: Wallet, iconBg: 'bg-emerald-500/20', iconColor: 'text-emerald-400', label: 'Total Revenue (MTD)', value: inr(k.total_revenue_mtd.value), deltaPct: k.total_revenue_mtd.delta_pct, deltaLabel: k.total_revenue_mtd.compare_label },
        ]),
        { icon: CalendarIcon, iconBg: 'bg-blue-500/20', iconColor: 'text-blue-400', label: 'Total Bookings (Today)', value: String(k.total_bookings_today.value), deltaPct: k.total_bookings_today.delta_pct, deltaLabel: k.total_bookings_today.compare_label },
        { icon: Clock, iconBg: 'bg-amber-500/20', iconColor: 'text-amber-400', label: 'Court Utilization', value: `${k.court_utilization.value}%`, deltaPct: k.court_utilization.delta_pct, deltaLabel: k.court_utilization.compare_label },
        { icon: TrendingUp, iconBg: 'bg-violet-500/20', iconColor: 'text-violet-400', label: 'Avg. Revenue / Hour', value: inr(k.avg_revenue_per_hour.value), deltaPct: k.avg_revenue_per_hour.delta_pct, deltaLabel: k.avg_revenue_per_hour.compare_label },
        { icon: Activity, iconBg: 'bg-rose-500/20', iconColor: 'text-rose-400', label: 'Peak Hour Occupancy', value: `${k.peak_hour_occupancy.value}%`, deltaPct: null, deltaLabel: k.peak_hour_occupancy.peak_hour },
      ]
    : [];

  // ---- Pie data ----
  const pieData = (data?.bookings_by_source?.items || []).map((it, i) => ({
    name: it.name,
    value: it.count,
    pct: it.pct,
    color: SOURCE_COLORS[it.name] || fallbackColors[i % fallbackColors.length],
  }));
  const pieTotal = data?.bookings_by_source?.total || 0;

  // ---- Business Amount by Source pie data ----
  const amountPieData = (data?.business_amount_by_source?.items || []).map((it, i) => ({
    name: it.name,
    value: it.amount,
    pct: it.pct,
    color: SOURCE_COLORS[it.name] || fallbackColors[i % fallbackColors.length],
  }));
  const amountPieTotal = data?.business_amount_by_source?.total || 0;

  // ---- Inventory donut data ----
  const invPct = data?.inventory_utilization?.utilized_pct || 0;
  const invData = [
    { name: 'utilized', value: invPct, fill: invPct >= 70 ? '#10b981' : invPct >= 40 ? '#f59e0b' : '#10b981' },
    { name: 'remaining', value: Math.max(0, 100 - invPct), fill: isDark ? '#1e293b' : '#e2e8f0' },
  ];

  return (
    <DashboardLayout>
      <div
        className={`min-h-full -mx-4 sm:-mx-6 lg:-mx-8 -mt-8 px-4 sm:px-6 lg:px-8 pt-6 pb-8 ${themeBg}`}
        data-testid="dashboard-page"
      >
        {/* Top bar */}
        <div className={`flex items-start justify-between flex-wrap gap-3 mb-5 ${themeText}`}>
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">
              Dashboard <span className={`${themeMuted} text-base font-normal`}>Overview &amp; Performance</span>
            </h1>
            {data && (
              <p className={`text-xs ${themeMuted} mt-1`}>
                {data.range.start} → {data.range.end}
                {venue !== 'all' && ` · ${venue}`}
                {' · '}refreshed {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : '—'}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={range} onValueChange={setRange} disabled={isStaff}>
              <SelectTrigger
                className={`w-[180px] ${isDark ? 'bg-slate-900 border-slate-700 text-white' : ''} ${isStaff ? 'opacity-70 cursor-not-allowed' : ''}`}
                data-testid="dashboard-range-select"
                title={isStaff ? 'Staff view is locked to Today' : ''}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGE_PRESETS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {range === 'custom' && (
              <div className="flex items-center gap-1.5" data-testid="dashboard-custom-range">
                <input
                  type="date"
                  value={customStart}
                  max={customEnd}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className={`h-9 px-2 rounded-md border text-xs ${
                    isDark
                      ? 'bg-slate-900 border-slate-700 text-white [color-scheme:dark]'
                      : 'bg-white border-slate-200 text-slate-900'
                  }`}
                  data-testid="dashboard-custom-start"
                />
                <span className={`text-xs ${themeMuted}`}>→</span>
                <input
                  type="date"
                  value={customEnd}
                  min={customStart}
                  max={istDateStr()}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className={`h-9 px-2 rounded-md border text-xs ${
                    isDark
                      ? 'bg-slate-900 border-slate-700 text-white [color-scheme:dark]'
                      : 'bg-white border-slate-200 text-slate-900'
                  }`}
                  data-testid="dashboard-custom-end"
                />
              </div>
            )}
            <Select value={venue} onValueChange={setVenue}>
              <SelectTrigger
                className={`w-[180px] ${isDark ? 'bg-slate-900 border-slate-700 text-white' : ''}`}
                data-testid="dashboard-venue-select"
              >
                <SelectValue placeholder="All Venues" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Venues</SelectItem>
                {venueOptions.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={timeSlot} onValueChange={setTimeSlot}>
              <SelectTrigger
                className={`w-[180px] ${isDark ? 'bg-slate-900 border-slate-700 text-white' : ''}`}
                data-testid="dashboard-timeslot-select"
              >
                <SelectValue placeholder="All Hours" />
              </SelectTrigger>
              <SelectContent>
                {TIME_SLOT_PRESETS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={bookingSource} onValueChange={setBookingSource}>
              <SelectTrigger
                className={`w-[160px] ${isDark ? 'bg-slate-900 border-slate-700 text-white' : ''}`}
                data-testid="dashboard-source-select"
              >
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                {BOOKING_SOURCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={bulkFilter} onValueChange={setBulkFilter}>
              <SelectTrigger
                className={`w-[190px] ${isDark ? 'bg-slate-900 border-slate-700 text-white' : ''}`}
                data-testid="dashboard-bulk-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BULK_FILTER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => setIsDark((v) => !v)}
              variant="outline"
              size="icon"
              className={isDark ? 'border-slate-700 text-white hover:bg-slate-800' : ''}
              data-testid="dashboard-theme-toggle"
              title={isDark ? 'Switch to light' : 'Switch to dark'}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {loading && !data && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className={`w-8 h-8 animate-spin ${themeMuted}`} />
          </div>
        )}

        {data && (
          <>
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
              {kpiData.map((kp) => (
                <KpiCard key={kp.label} {...kp} isDark={isDark} />
              ))}
            </div>

            {/* Cancellations strip */}
            {data.cancellations_summary && (
              <Card className={`p-4 mb-5 ${themeCard} border`} data-testid="widget-cancellations">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <CalendarX className="w-5 h-5 text-rose-500" />
                    <h3 className={`text-sm font-semibold ${themeText}`}>
                      Cancellations
                      <span className={`ml-2 text-[10px] ${themeMuted} font-normal`}>
                        ({RANGE_PRESETS.find((r) => r.value === range)?.label})
                      </span>
                    </h3>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="text-center">
                      <p className="text-xl font-bold text-cyan-500" data-testid="dashboard-cancellations-platform">{data.cancellations_summary.platform}</p>
                      <p className={`text-[10px] ${themeMuted}`}>Platform</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-violet-500" data-testid="dashboard-cancellations-manual">{data.cancellations_summary.manual}</p>
                      <p className={`text-[10px] ${themeMuted}`}>Manual</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-xl font-bold ${themeText}`}>{data.cancellations_summary.total}</p>
                      <p className={`text-[10px] ${themeMuted}`}>Total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-rose-500">{inr(data.cancellations_summary.amount)}</p>
                      <p className={`text-[10px] ${themeMuted}`}>Value Lost</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className={isDark ? 'border-slate-700 text-white hover:bg-slate-800' : ''}
                      onClick={() => navigate('/office/cancellations')}
                      data-testid="dashboard-view-cancellations"
                    >
                      View all <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Row 2: Day Book Data | revenue area | sources donut */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
              {/* Day Book Data */}
              <Card className={`lg:col-span-1 p-4 ${themeCard} border`} data-testid="widget-day-book-data">
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-semibold ${themeText}`}>Day Book Data</h3>
                  <span className={`text-[10px] ${themeMuted}`}>
                    {data.day_book_data.row_count} {data.day_book_data.row_count === 1 ? 'row' : 'rows'}
                  </span>
                </div>
                {data.day_book_data.row_count === 0 ? (
                  <p className={`text-xs ${themeMuted} text-center py-12`}>
                    No Day Book entries match the current filters.
                  </p>
                ) : (
                  <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className={themeMuted}>
                          <th className="px-1 py-1 text-left font-medium">Date</th>
                          {!venue || venue === 'all' ? (
                            <th className="px-1 py-1 text-left font-medium">Venue</th>
                          ) : null}
                          <th className="px-1 py-1 text-right font-medium">KM</th>
                          <th className="px-1 py-1 text-right font-medium">Dist</th>
                          <th className="px-1 py-1 text-right font-medium">Hudle</th>
                          <th className="px-1 py-1 text-right font-medium">Cash</th>
                          <th className="px-1 py-1 text-right font-medium">POS</th>
                          <th className="px-1 py-1 text-right font-medium">Other</th>
                          <th className="px-1 py-1 text-right font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.day_book_data.rows.slice(0, 12).map((r, i) => (
                          <tr
                            key={`${r.date}-${r.venue}-${i}`}
                            className={`border-t ${isDark ? 'border-slate-800' : 'border-slate-100'}`}
                          >
                            <td className={`px-1 py-1 ${themeText} whitespace-nowrap`}>{r.date?.slice(5)}</td>
                            {!venue || venue === 'all' ? (
                              <td className={`px-1 py-1 ${themeMuted} truncate max-w-[80px]`} title={r.venue}>{r.venue}</td>
                            ) : null}
                            <td className={`px-1 py-1 text-right ${themeText}`}>{r.km > 0 ? Math.round(r.km).toLocaleString('en-IN') : <span className={themeMuted}>—</span>}</td>
                            <td className={`px-1 py-1 text-right ${themeText}`}>{r.district > 0 ? Math.round(r.district).toLocaleString('en-IN') : <span className={themeMuted}>—</span>}</td>
                            <td className={`px-1 py-1 text-right ${themeText}`}>{r.hudle > 0 ? Math.round(r.hudle).toLocaleString('en-IN') : <span className={themeMuted}>—</span>}</td>
                            <td className={`px-1 py-1 text-right ${themeText}`}>{r.cash > 0 ? Math.round(r.cash).toLocaleString('en-IN') : <span className={themeMuted}>—</span>}</td>
                            <td className={`px-1 py-1 text-right ${themeText}`}>{r.pos > 0 ? Math.round(r.pos).toLocaleString('en-IN') : <span className={themeMuted}>—</span>}</td>
                            <td className={`px-1 py-1 text-right ${themeText}`}>{r.other > 0 ? Math.round(r.other).toLocaleString('en-IN') : <span className={themeMuted}>—</span>}</td>
                            <td className={`px-1 py-1 text-right font-semibold ${themeText}`}>{inr(r.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-300'} font-bold`}>
                          <td className={`px-1 py-1 ${themeText}`} colSpan={!venue || venue === 'all' ? 2 : 1}>Total</td>
                          <td className={`px-1 py-1 text-right ${themeText}`}>{Math.round(data.day_book_data.grand_total.km).toLocaleString('en-IN')}</td>
                          <td className={`px-1 py-1 text-right ${themeText}`}>{Math.round(data.day_book_data.grand_total.district).toLocaleString('en-IN')}</td>
                          <td className={`px-1 py-1 text-right ${themeText}`}>{Math.round(data.day_book_data.grand_total.hudle).toLocaleString('en-IN')}</td>
                          <td className={`px-1 py-1 text-right ${themeText}`}>{Math.round(data.day_book_data.grand_total.cash).toLocaleString('en-IN')}</td>
                          <td className={`px-1 py-1 text-right ${themeText}`}>{Math.round(data.day_book_data.grand_total.pos).toLocaleString('en-IN')}</td>
                          <td className={`px-1 py-1 text-right ${themeText}`}>{Math.round(data.day_book_data.grand_total.other).toLocaleString('en-IN')}</td>
                          <td className="px-1 py-1 text-right text-emerald-500">{inr(data.day_book_data.grand_total.total)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
                {data.day_book_data.row_count > 12 && (
                  <p className={`text-[10px] ${themeMuted} text-right mt-1`}>
                    Showing 12 of {data.day_book_data.row_count} — totals reflect all.
                  </p>
                )}
              </Card>

              {/* Revenue Area */}
              <Card className={`p-4 ${themeCard} border`} data-testid="widget-revenue-overview">
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-semibold ${themeText}`}>Revenue Overview</h3>
                  <span className={`text-[10px] ${themeMuted}`}>{RANGE_PRESETS.find((r) => r.value === range)?.label}</span>
                </div>
                <p className={`text-2xl font-bold ${themeText}`}>{inr(data.revenue_overview.total)}</p>
                <DeltaPill pct={data.revenue_overview.delta_pct} suffix="vs prev period" isDark={isDark} />
                <div className="h-44 mt-2 -ml-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.revenue_overview.series}>
                      <defs>
                        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.6} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }}
                        tickFormatter={(d) => d.slice(5)}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }}
                        tickFormatter={inrK}
                        axisLine={false}
                        tickLine={false}
                        width={42}
                      />
                      <Tooltip
                        contentStyle={{
                          background: isDark ? '#0f172a' : '#fff',
                          borderRadius: 8,
                          border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                          fontSize: 12,
                        }}
                        formatter={(v) => [inr(v), 'Revenue']}
                      />
                      <Area type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={2} fill="url(#revGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Bookings by Source */}
              <Card className={`p-4 ${themeCard} border`} data-testid="widget-bookings-source">
                <h3 className={`text-sm font-semibold ${themeText} mb-3`}>Bookings by Source</h3>
                {pieTotal === 0 ? (
                  <p className={`text-sm ${themeMuted} text-center py-10`}>No bookings in selected range.</p>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="relative w-32 h-32 shrink-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={pieData} dataKey="value" innerRadius={36} outerRadius={56} paddingAngle={2}>
                            {pieData.map((d, i) => (
                              <Cell key={i} fill={d.color} stroke="none" />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <p className={`text-xl font-bold ${themeText}`}>{pieTotal}</p>
                        <p className={`text-[10px] ${themeMuted}`}>Total</p>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      {pieData.slice(0, 5).map((d) => (
                        <div key={d.name} className="flex items-center gap-2 text-xs">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }}></span>
                          <span className={`flex-1 truncate ${themeText}`}>{d.name}</span>
                          <span className={`${themeMuted} text-[10px]`}>{d.pct}% ({d.value})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Business Amount by Source */}
                <div className={`mt-5 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`} data-testid="widget-business-amount-source">
                  <h3 className={`text-sm font-semibold ${themeText} mb-3`}>Business Amount by Source</h3>
                  {amountPieTotal === 0 ? (
                    <p className={`text-sm ${themeMuted} text-center py-10`}>No business amount in selected range.</p>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="relative w-32 h-32 shrink-0">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={amountPieData} dataKey="value" innerRadius={36} outerRadius={56} paddingAngle={2}>
                              {amountPieData.map((d, i) => (
                                <Cell key={i} fill={d.color} stroke="none" />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-2 text-center">
                          <p className={`text-sm font-bold ${themeText} leading-tight`}>{inrK(amountPieTotal)}</p>
                          <p className={`text-[10px] ${themeMuted}`}>Total</p>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        {amountPieData.slice(0, 5).map((d) => (
                          <div key={d.name} className="flex items-center gap-2 text-xs">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }}></span>
                            <span className={`flex-1 truncate ${themeText}`}>{d.name}</span>
                            <span className={`${themeMuted} text-[10px]`}>{d.pct}% ({inr(d.value)})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Row 3: time-slot performance | court-wise revenue | inventory donut */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
              {/* Time Slot Performance */}
              <Card className={`p-4 ${themeCard} border`} data-testid="widget-time-slot-perf">
                <h3 className={`text-sm font-semibold ${themeText} mb-3`}>Time Slot Performance</h3>
                <div className="space-y-2">
                  {data.time_slot_performance.slots.map((s) => (
                    <div key={s.slot}>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className={themeMuted}>{s.slot}</span>
                        <span className={`font-medium ${themeText}`}>{s.utilization_pct}%</span>
                      </div>
                      <div className={`h-1.5 rounded-full ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                        <div
                          className={`h-full rounded-full ${
                            s.utilization_pct >= 70 ? 'bg-emerald-500' : s.utilization_pct >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                          }`}
                          style={{ width: `${Math.min(100, s.utilization_pct)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {data.time_slot_performance.best && (
                    <div className="p-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-500 flex items-center gap-1">
                        <Star className="w-3 h-3" /> Best
                      </p>
                      <p className={`text-xs font-semibold ${themeText}`}>{data.time_slot_performance.best.slot}</p>
                      <p className="text-xs text-emerald-500 font-bold">{data.time_slot_performance.best.utilization_pct}%</p>
                    </div>
                  )}
                  {data.time_slot_performance.worst && (
                    <div className="p-2 rounded-lg border border-rose-500/30 bg-rose-500/10">
                      <p className="text-[10px] uppercase tracking-wider text-rose-500 flex items-center gap-1">
                        <Activity className="w-3 h-3" /> Lowest
                      </p>
                      <p className={`text-xs font-semibold ${themeText}`}>{data.time_slot_performance.worst.slot}</p>
                      <p className="text-xs text-rose-500 font-bold">{data.time_slot_performance.worst.utilization_pct}%</p>
                    </div>
                  )}
                </div>
              </Card>

              {/* Court-wise Revenue Table */}
              <Card className={`p-4 ${themeCard} border`} data-testid="widget-court-wise-rev">
                <h3 className={`text-sm font-semibold ${themeText} mb-3`}>
                  {data.court_wise_revenue.grouping === 'court' ? 'Top Courts (Revenue)' : 'Court-wise Revenue'}
                  <span className={`ml-2 text-[10px] ${themeMuted} font-normal`}>
                    ({RANGE_PRESETS.find((r) => r.value === range)?.label})
                  </span>
                </h3>
                <table className="w-full text-xs">
                  <thead>
                    <tr className={`${themeMuted} text-left`}>
                      <th className="pb-2 font-medium">
                        {data.court_wise_revenue.grouping === 'court' ? 'Court' : 'Venue'}
                      </th>
                      <th className="pb-2 text-right font-medium">Revenue</th>
                      <th className="pb-2 text-right font-medium">Util</th>
                      <th className="pb-2 text-right font-medium">Rev/Hr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.court_wise_revenue.grouping === 'court'
                      ? data.court_wise_revenue.court_rows.slice(0, 4).map((r) => (
                          <tr key={r.court} className={`border-t ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
                            <td className={`py-2 ${themeText}`}>
                              {r.court}
                              <span className={`block text-[10px] ${themeMuted}`}>{r.venue}</span>
                            </td>
                            <td className={`py-2 text-right ${themeText}`}>{inr(r.revenue)}</td>
                            <td className={`py-2 text-right ${themeText}`}>{r.utilization_pct}%</td>
                            <td className={`py-2 text-right ${themeText}`}>{inr(r.revenue_per_hour)}</td>
                          </tr>
                        ))
                      : data.court_wise_revenue.rows.slice(0, 5).map((r) => (
                          <tr key={r.venue} className={`border-t ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
                            <td className={`py-2 ${themeText}`}>
                              {r.venue}
                              <span className={`block text-[10px] ${themeMuted}`}>{r.court_count} {r.court_count === 1 ? 'court' : 'courts'}</span>
                            </td>
                            <td className={`py-2 text-right ${themeText}`}>{inr(r.revenue)}</td>
                            <td className={`py-2 text-right ${themeText}`}>{r.utilization_pct}%</td>
                            <td className={`py-2 text-right ${themeText}`}>{inr(r.revenue_per_hour)}</td>
                          </tr>
                        ))}
                    <tr className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-200'} font-semibold text-blue-500`}>
                      <td className="py-2">Total / Avg</td>
                      <td className="py-2 text-right">{inr(data.court_wise_revenue.total_revenue)}</td>
                      <td className="py-2 text-right">{data.court_wise_revenue.total_utilization_pct}%</td>
                      <td className="py-2 text-right">{inr(data.court_wise_revenue.avg_revenue_per_hour)}</td>
                    </tr>
                  </tbody>
                </table>
              </Card>

              {/* Inventory Utilization Donut */}
              <Card className={`p-4 ${themeCard} border`} data-testid="widget-inventory">
                <h3 className={`text-sm font-semibold ${themeText} mb-3`}>Inventory Utilization (Today)</h3>
                <div className="flex items-center gap-3">
                  <div className="relative w-32 h-32 shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={invData} dataKey="value" innerRadius={38} outerRadius={56} startAngle={90} endAngle={-270}>
                          {invData.map((d, i) => (<Cell key={i} fill={d.fill} stroke="none" />))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <p className={`text-2xl font-bold ${themeText}`}>{invPct}%</p>
                      <p className={`text-[10px] ${themeMuted}`}>Utilized</p>
                    </div>
                  </div>
                  <div className="flex-1 space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className={themeMuted}>Total Available Hours</span>
                      <span className={`font-semibold ${themeText}`}>{data.inventory_utilization.total_hours} hrs</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={themeMuted}>Booked Hours</span>
                      <span className="font-semibold text-emerald-500">{data.inventory_utilization.booked_hours} hrs</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={themeMuted}>Unsold Hours</span>
                      <span className="font-semibold text-rose-500">{data.inventory_utilization.unsold_hours} hrs</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className={`w-full mt-3 ${isDark ? 'border-blue-500/40 text-blue-400 hover:bg-blue-500/10' : ''}`}
                  onClick={() => navigate('/office/occupancy')}
                  data-testid="dashboard-view-empty-slots"
                >
                  <Eye className="w-4 h-4 mr-2" /> View Empty Slots
                </Button>
              </Card>
            </div>

            {/* Row 4: customer insights | recent bookings | alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Customer Insights */}
              <Card className={`p-4 ${themeCard} border`} data-testid="widget-customer-insights">
                <h3 className={`text-sm font-semibold ${themeText} mb-3`}>
                  Customer Insights
                  <span className={`ml-2 text-[10px] ${themeMuted} font-normal`}>
                    ({RANGE_PRESETS.find((r) => r.value === range)?.label})
                  </span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { icon: Users, label: 'Active Users', value: data.customer_insights.active_users.value, delta: data.customer_insights.active_users.delta_pct, color: 'text-blue-400' },
                    { icon: Repeat, label: 'Repeat Users', value: data.customer_insights.repeat_users.value, delta: data.customer_insights.repeat_users.delta_pct, color: 'text-emerald-400' },
                    { icon: UserPlus, label: 'New Users', value: data.customer_insights.new_users.value, delta: data.customer_insights.new_users.delta_pct, color: 'text-violet-400' },
                    { icon: Target, label: 'Retention Rate', value: `${data.customer_insights.retention_rate.value}%`, delta: data.customer_insights.retention_rate.delta_pct, color: 'text-amber-400' },
                  ].map((m) => (
                    <div key={m.label} className="text-center">
                      <m.icon className={`w-6 h-6 mx-auto ${m.color} mb-1`} />
                      <p className={`text-xl font-bold ${themeText}`}>{m.value}</p>
                      <p className={`text-[10px] ${themeMuted} truncate`}>{m.label}</p>
                      {m.delta !== null && m.delta !== undefined && (
                        <p className={`text-[10px] ${m.delta >= 0 ? 'text-emerald-500' : 'text-rose-500'} font-medium`}>
                          {m.delta >= 0 ? '↑' : '↓'} {Math.abs(m.delta)}%
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Average Ticket Size by Game */}
              <Card className={`p-4 ${themeCard} border`} data-testid="widget-avg-ticket-size">
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-semibold ${themeText}`}>Avg. Ticket Size by Game</h3>
                  <span className={`text-[10px] ${themeMuted}`}>Revenue ÷ Hours Booked</span>
                </div>
                <div className="space-y-2">
                  {(!data.avg_ticket_size_by_game || data.avg_ticket_size_by_game.length === 0) && (
                    <p className={`text-xs ${themeMuted} py-4 text-center`}>No bookings for the selected filters.</p>
                  )}
                  {(data.avg_ticket_size_by_game || []).map((g, i) => (
                    <div
                      key={g.game}
                      className={`flex items-center justify-between gap-2 p-2 rounded ${isDark ? 'hover:bg-slate-800/60' : 'hover:bg-slate-50'}`}
                      data-testid={`avg-ticket-size-row-${g.game}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: GAME_COLORS[i % GAME_COLORS.length] }} />
                        <p className={`text-xs ${themeText} truncate`}>{g.game}</p>
                      </div>
                      <p className={`text-[10px] ${themeMuted} shrink-0`}>{g.hours} hrs</p>
                      <p className={`text-xs font-semibold ${themeText} shrink-0`}>{inr(g.avg_ticket_per_hour)}/hr</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Alerts */}
              <Card className={`p-4 ${themeCard} border`} data-testid="widget-alerts">
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-semibold ${themeText} flex items-center gap-2`}>
                    <Bell className="w-4 h-4" /> Alerts &amp; Action Panel
                  </h3>
                </div>
                <div className="space-y-2">
                  {data.alerts.length === 0 && (
                    <p className={`text-xs ${themeMuted} py-4 text-center`}>No alerts. All clear.</p>
                  )}
                  {data.alerts.map((a, i) => {
                    const Icon = a.severity === 'critical' ? ShieldAlert : a.severity === 'info' ? CreditCard : AlertTriangle;
                    const tone =
                      a.severity === 'critical'
                        ? 'text-rose-500 bg-rose-500/10 border-rose-500/30'
                        : a.severity === 'info'
                        ? 'text-blue-500 bg-blue-500/10 border-blue-500/30'
                        : 'text-amber-500 bg-amber-500/10 border-amber-500/30';
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => a.action_url && navigate(a.action_url)}
                        className={`w-full flex items-start gap-2 p-2 rounded-lg border ${tone} hover:opacity-90 transition text-left`}
                        data-testid={`alert-${i}`}
                      >
                        <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs font-medium ${themeText}`}>{a.title}</p>
                          <p className={`text-[10px] ${themeMuted}`}>{a.detail}</p>
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-60" />
                      </button>
                    );
                  })}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
